import { Field, Form, Formik } from 'formik';
import { TFunction } from 'i18next';
import React from 'react';
import { CHARCOAL_GREY, LIGHT_BLUE } from '~/Constants/Colors.constants';
import { userProfileValidationScheme } from '~/Helpers/Validators.helper';
import IUser from '~/Models/DTO/User.model';
import { Button, TextField, Typography, Divider } from '~/UIKit';
import { User, Password, AtSign } from '~/Components/Icons';
import { makeStyles } from '~/UIKit/Style';
import { UserProfileNotificationSettings } from './UserProfileNotificationSettings';
import { useHasAccess, useUser } from '~/Hooks';
import { AccessList } from '~/Hooks/useHasAccess/accessList.types';
import { UserProfileMockAccessList } from './UserProfileMockAccessList/UserProfileMockAccessList';

const useStyles = makeStyles((theme) => ({
  form: {
    color: CHARCOAL_GREY,
    [theme.breakpoints.up('md')]: {
      paddingRight: theme.spacing(1),
    },
  },
  passwordSection: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 50,
  },
  formLabel: {
    flex: 1,
  },
  withIcon: {
    width: 30,
    height: 30,
    backgroundColor: LIGHT_BLUE,
    borderRadius: 30,
    marginRight: 15,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&>svg': {
      fontSize: 20,
    },
  },
}));

export interface IUserProfileDetailProps {
  profileInitialValues: Partial<IUser>;
  onValidateProfileForm: (data: Partial<IUser>) => void;
  handleChangePasswordNavigation: () => void;
  t: TFunction;
}

export const UserProfileDetail = ({
  profileInitialValues,
  onValidateProfileForm,
  handleChangePasswordNavigation,
  t,
}: IUserProfileDetailProps) => {
  const { classes } = useStyles();
  const { hasAccess } = useHasAccess();
  const { DANGEROUSLY_isSuperAdmin } = useUser();
  return (
    <>
      <Formik
        onSubmit={() => {}}
        initialValues={profileInitialValues}
        validate={onValidateProfileForm}
        validationSchema={userProfileValidationScheme}
      >
        {({ errors, touched }) => (
          <Form className={classes.form}>
            <Divider />
            <Field
              component={TextField}
              icon={<User />}
              id="first_name"
              label={t('app.firstName')}
              name="first_name"
              placeholder={t('app.firstName')}
              type="text"
              inputProps={{ maxLength: 50 }}
              newDesign
              error={touched.first_name && Boolean(errors.first_name)}
              helperText={touched.first_name && errors.first_name}
            />
            <Divider />
            <Field
              component={TextField}
              icon={<User />}
              id="last_name"
              inputProps={{ maxLength: 50 }}
              label={t('app.lastName')}
              name="last_name"
              placeholder={t('app.lastName')}
              type="text"
              newDesign
              error={touched.last_name && Boolean(errors.last_name)}
              helperText={touched.last_name && errors.last_name}
            />
            <Divider />
            <Field
              component={TextField}
              icon={<AtSign />}
              id="email"
              label={t('app.email')}
              placeholder={t('app.email')}
              name="email"
              type="text"
              newDesign
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
            />
          </Form>
        )}
      </Formik>
      <Divider />
      <div className={classes.passwordSection}>
        <div className={classes.withIcon}>
          <Password />
        </div>

        <Typography className={classes.formLabel}>{t('app.password')}</Typography>
        <Button
          variant="text"
          color="primary"
          type="button"
          onClick={handleChangePasswordNavigation}
        >
          {t('app.user.changePassword')}
        </Button>
      </div>
      <Divider />
      {hasAccess(AccessList.USER_NOTIFICATIONS) && <UserProfileNotificationSettings />}
      {/* we can't use the access list here because we don't want it to get overridden by the mocks */}
      {DANGEROUSLY_isSuperAdmin && <UserProfileMockAccessList />}
    </>
  );
};
